import {
	type ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioInvestmentTypeEnum,
	type CurrencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	type NormalizedInstruction,
	type NormalizedTool,
	type NormalizedToolOfKind,
	ToolKind,
} from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

const PageResources = Resources.PortfolioDetail;

export type ToolHistoryItem = {
	id: number;
	isBuy: boolean;
	directionLabel: string;
	transactionLabel: string;
	amount: string;
	startDate: string;
	endDate: string;
	dates: string;
	frequency: string;
};

type Translate = (key: string) => string;

type ToolHistoryVariant = Pick<
	ToolHistoryItem,
	"isBuy" | "directionLabel" | "transactionLabel" | "amount"
>;

type InstructionOf<K extends ToolKind> =
	NormalizedToolOfKind<K>["instructions"][number];

type VariantBuilder<K extends ToolKind> = (
	instruction: InstructionOf<K>,
	currencySign: string,
	t: Translate,
) => ToolHistoryVariant;

const actionLabel = (
	t: Translate,
	type?: ClientPortfolioInvestmentTypeEnum | null,
) =>
	t(
		PageResources.ToolHistory.ActionType[
			type ?? ClientPortfolioInvestmentTypeEnum.Buy
		],
	);

const currencyLabel = (t: Translate, currency: CurrencyEnum) =>
	t(PageResources.SharedLabels.Currency[currency]);

const frequencyLabel = (
	t: Translate,
	frequency: ClientPortfolioInvestmentFrequencyEnum,
) => t(PageResources.ToolHistory.Fund.Frequency[frequency]);

const dateRange = (startAt?: string | null, endAt?: string | null) => {
	const start = dateFormatter(startAt);
	const end = dateFormatter(endAt);
	return end ? `${start}, ${end}` : start;
};

const isPurchase = (instruction: NormalizedInstruction<unknown>) =>
	instruction.investmentType !== ClientPortfolioInvestmentTypeEnum.Sell;

const investmentVariant: VariantBuilder<typeof ToolKind.Investment> = (
	{ investmentType, raw },
	_currencySign,
	t,
) => ({
	isBuy: investmentType !== ClientPortfolioInvestmentTypeEnum.Sell,
	directionLabel: actionLabel(t, investmentType),
	transactionLabel: raw.transactionType
		? t(PageResources.ToolHistory.Fund.TransactionType[raw.transactionType])
		: "",
	amount: moneyFormatter(raw.amount, currencyLabel(t, raw.currency)),
});

const bondVariant: VariantBuilder<typeof ToolKind.Bond> = (
	{ raw },
	_currencySign,
	t,
) => ({
	isBuy: true,
	directionLabel: t(PageResources.ToolHistory.purchase),
	transactionLabel: "",
	amount: moneyFormatter(raw.price, currencyLabel(t, raw.currency)),
});

const cryptoVariant: VariantBuilder<typeof ToolKind.Crypto> = (
	instruction,
	_currencySign,
	t,
) => ({
	isBuy: isPurchase(instruction),
	directionLabel: actionLabel(t, instruction.investmentType),
	transactionLabel: "",
	amount: moneyFormatter(
		instruction.raw.countFrom,
		instruction.raw.currencyFrom,
	),
});

const commodityVariant: VariantBuilder<typeof ToolKind.Commodity> = (
	instruction,
	currencySign,
	t,
) => ({
	isBuy: isPurchase(instruction),
	directionLabel: actionLabel(t, instruction.investmentType),
	transactionLabel: "",
	amount: moneyFormatter(
		instruction.raw.amount,
		instruction.raw.currency
			? currencyLabel(t, instruction.raw.currency)
			: currencySign,
	),
});

const bankProductVariant: VariantBuilder<typeof ToolKind.BankProduct> = (
	instruction,
	currencySign,
	t,
) => {
	const { investmentType, raw } = instruction;
	const hasDirection =
		investmentType !== null &&
		investmentType !== ClientPortfolioInvestmentTypeEnum.Empty;

	return {
		isBuy: isPurchase(instruction),
		directionLabel: hasDirection
			? t(PageResources.ToolHistory.ActionTypeBankProduct[investmentType])
			: "",
		transactionLabel: "",
		amount: moneyFormatter(
			raw.amount,
			raw.currency ? currencyLabel(t, raw.currency) : currencySign,
		),
	};
};

export const buildToolHistoryItems = (
	tool: NormalizedTool,
	currencySign: string,
	t: Translate,
): ToolHistoryItem[] => {
	const toItems = <K extends ToolKind>(
		instructions: readonly InstructionOf<K>[],
		buildVariant: VariantBuilder<K>,
	): ToolHistoryItem[] =>
		instructions.map((instruction) => ({
			id: instruction.id,
			...buildVariant(instruction, currencySign, t),
			startDate: dateFormatter(instruction.startAt),
			endDate: dateFormatter(instruction.endAt),
			dates: dateRange(instruction.startAt, instruction.endAt),
			frequency: frequencyLabel(t, instruction.frequency),
		}));

	switch (tool.kind) {
		case ToolKind.Investment:
			return toItems(tool.instructions, investmentVariant);
		case ToolKind.Bond:
			return toItems(tool.instructions, bondVariant);
		case ToolKind.Crypto:
			return toItems(tool.instructions, cryptoVariant);
		case ToolKind.Commodity:
			return toItems(tool.instructions, commodityVariant);
		case ToolKind.BankProduct:
			return toItems(tool.instructions, bankProductVariant);
	}
};

export const useToolHistoryItems = (
	tool: NormalizedTool,
	currencySign: string,
): ToolHistoryItem[] => {
	const { t } = useResource();

	return buildToolHistoryItems(tool, currencySign, t);
};
